/* Assets: section-video-carousel.js */

if (!customElements.get('video-carousel')) {
  customElements.define(
    'video-carousel',
    class VideoCarousel extends HTMLElement {
      constructor() {
        super();
        this.swiper = null;
        this.isMuted = true;
      }

      connectedCallback() {
        this.initWhenReady();
        this.initEventListeners();
      }

      disconnectedCallback() {
        if (this.swiper) {
          this.swiper.destroy(true, true);
        }
      }

      initWhenReady() {
        if (typeof Swiper !== 'undefined') {
          this.initSwiper();
        } else {
          // Poll until Swiper is available if loaded asynchronously
          const checkSwiper = setInterval(() => {
            if (typeof Swiper !== 'undefined') {
              clearInterval(checkSwiper);
              this.initSwiper();
            }
          }, 100);
          setTimeout(() => clearInterval(checkSwiper), 5000);
        }
      }

      initSwiper() {
        const swiperContainer = this.querySelector('.video-carousel__swiper');
        const prevEl = this.querySelector('.video-carousel__nav--prev');
        const nextEl = this.querySelector('.video-carousel__nav--next');
        const slides = this.querySelectorAll('.video-carousel__slide');

        if (!swiperContainer || slides.length === 0) return;

        const isLooping = slides.length > 1;

        this.swiper = new Swiper(swiperContainer, {
          slidesPerView: 1.65,
          centeredSlides: true,
          spaceBetween: 16,
          loop: isLooping,
          grabCursor: true,
          speed: 550,
          watchSlidesProgress: true,
          preventClicks: false,
          preventClicksPropagation: false,
          touchStartPreventDefault: false,
          noSwiping: true,
          noSwipingSelector: '.video-carousel__add-btn, .video-carousel__mute-btn, .video-carousel__product-overlay a, product-form',
          keyboard: {
            enabled: true,
          },
          navigation: {
            prevEl: prevEl,
            nextEl: nextEl,
          },
          breakpoints: {
            480: {
              slidesPerView: 1.8,
              spaceBetween: 20,
            },
            750: {
              slidesPerView: 2.5,
              spaceBetween: 24,
            },
            990: {
              slidesPerView: 3.3,
              spaceBetween: 28,
            },
            1200: {
              slidesPerView: 4.1,
              spaceBetween: 32,
            },
          },
          on: {
            init: (swiper) => {
              this.handleVideoPlayback();
              this.updateSlideScaling(swiper);
            },
            slideChangeTransitionEnd: (swiper) => {
              this.handleVideoPlayback();
              this.updateSlideScaling(swiper);
            },
            setTranslate: (swiper) => {
              this.updateSlideScaling(swiper);
            },
            setTransition: (swiper, duration) => {
              this.setSlideTransitions(swiper, duration);
            },
          },
        });
      }

      updateSlideScaling(swiper) {
        if (!swiper || !swiper.slides) return;

        swiper.slides.forEach((slide) => {
          const progress = typeof slide.progress === 'number' ? slide.progress : (slide.classList.contains('swiper-slide-active') ? 0 : 1);
          const absProgress = Math.abs(progress);

          const scale = Math.max(0.82, Math.min(1.08, 1.08 - absProgress * 0.26));
          const opacity = Math.max(0.6, Math.min(1, 1 - absProgress * 0.4));

          slide.style.transform = `scale(${scale.toFixed(3)})`;
          slide.style.opacity = opacity.toFixed(3);
          slide.style.zIndex = Math.round(10 - absProgress * 5);
        });
      }

      setSlideTransitions(swiper, duration) {
        if (!swiper || !swiper.slides) return;
        swiper.slides.forEach((slide) => {
          slide.style.transition = `transform ${duration}ms cubic-bezier(0.25, 1, 0.5, 1), opacity ${duration}ms cubic-bezier(0.25, 1, 0.5, 1)`;
        });
      }

      initEventListeners() {
        // Handle Mute/Unmute buttons
        this.querySelectorAll('.video-carousel__mute-btn').forEach((btn) => {
          btn.addEventListener('click', (event) => {
            event.stopPropagation();
            this.toggleAudio(btn);
          });
          btn.addEventListener('pointerdown', (event) => {
            event.stopPropagation();
          });
        });

        // Prevent Swiper from intercepting interactions on the product overlay
        this.querySelectorAll('.video-carousel__product-overlay').forEach((overlay) => {
          overlay.addEventListener('pointerdown', (event) => {
            event.stopPropagation();
          });
        });

        // Handle active video card tap/click to play/pause (delegated)
        this.addEventListener('click', (event) => {
          if (event.target.closest('.video-carousel__product-overlay') ||
              event.target.closest('.video-carousel__mute-btn')) {
            return;
          }

          const card = event.target.closest('.video-carousel__card');
          if (card) {
            const slide = card.closest('.video-carousel__slide');
            if (slide && slide.classList.contains('swiper-slide-active')) {
              const video = card.querySelector('video');
              if (video) {
                if (video.paused) {
                  video.play();
                } else {
                  video.pause();
                }
              }
            }
          }
        });

        // Theme Editor support (Shopify customizer)
        document.addEventListener('shopify:block:select', (event) => {
          if (!this.swiper) return;
          const block = event.target;
          if (block && this.contains(block)) {
            const slideIndex = Array.from(this.querySelectorAll('.video-carousel__slide')).indexOf(block);
            if (slideIndex !== -1) {
              this.swiper.slideToLoop(slideIndex);
            }
          }
        });
      }

      handleVideoPlayback() {
        const slides = this.querySelectorAll('.video-carousel__slide');

        slides.forEach((slide) => {
          const video = slide.querySelector('video');
          if (!video) return;

          const isActive = slide.classList.contains('swiper-slide-active');

          if (isActive) {
            video.muted = this.isMuted;
            video.playsInline = true;

            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch((error) => {
                console.log('Video autoplay prevented:', error);
              });
            }
          } else {
            video.pause();
            video.currentTime = 0;
          }
        });
      }

      toggleAudio(muteBtn) {
        this.isMuted = !this.isMuted;

        const allMuteBtns = this.querySelectorAll('.video-carousel__mute-btn');
        allMuteBtns.forEach((btn) => {
          if (!this.isMuted) {
            btn.classList.add('is-unmuted');
          } else {
            btn.classList.remove('is-unmuted');
          }
        });

        const activeSlide = this.querySelector('.swiper-slide-active');
        if (activeSlide) {
          const video = activeSlide.querySelector('video');
          if (video) {
            video.muted = this.isMuted;
          }
        }
      }
    }
  );
}
